import { useIntl } from "react-intl";
import { useMatches } from "react-router";

import { Link } from "~/components/link";
import { Button } from "~/components/ui/button";
import { useURL } from "~/contexts";
import { basketHasItems } from "~/hec/cart-reminder/cart-reminder.logic";
import { useCartReminder } from "~/hec/cart-reminder/use-cart-reminder";
import { CartReminderToast } from "~/hec/components/molecules/cart-reminder-toast/cart-reminder-toast";
import { isMinicartHidden } from "~/lib/handle-utils";
import {
  useBasket,
  useBasketSettled,
} from "~/routes/resources/hooks/basket-hooks";

import messages from "./messages";
import type { CartReminderProps } from "./types";

/**
 * Organism: SFCC-12333 returning-shopper cart reminder, mounted in the header
 * beside the mini-cart. Page exclusions: the cart page opts out through the
 * same `handle.hideMinicart` flag that hides the mini-cart; checkout never
 * mounts it (its layout renders `SimplifiedHeader`, not `HecHeader`).
 *
 * The `role="status"` wrapper stays mounted while the card is hidden: screen
 * readers often skip a live region that is inserted already filled.
 */
export function CartReminder({ className }: CartReminderProps) {
  const intl = useIntl();
  const url = useURL();
  const { basket } = useBasket() ?? {};
  const settled = useBasketSettled();
  const excluded = isMinicartHidden(useMatches());
  const { visible, dismiss, hold, release } = useCartReminder({
    hasItems: basketHasItems(basket, settled),
    excluded,
  });

  return (
    <div role="status" className={className}>
      {visible ? (
        <CartReminderToast
          data-testid="header-cart-reminder-root"
          closeButtonTestId="header-cart-reminder-close-button"
          title={intl.formatMessage(messages.title)}
          message={intl.formatMessage(messages.message)}
          closeLabel={intl.formatMessage(messages.close)}
          onClose={dismiss}
          onPointerEnter={hold}
          onPointerLeave={release}
          onFocus={hold}
          onBlur={release}
          cta={
            <Button asChild variant="cta" size="2" className="w-full">
              <Link
                to={url("/cart")}
                onClick={dismiss}
                data-testid="header-cart-reminder-checkout-link"
              >
                {intl.formatMessage(messages.cta)}
              </Link>
            </Button>
          }
        />
      ) : null}
    </div>
  );
}
